"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Download, X } from "lucide-react"

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

const DISMISS_KEY = "pwa-install-dismissed"
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

// Only show PWA install prompt on these routes (not during active workflows)
const ALLOWED_ROUTES = ["/login", "/", "/admin/settings"]

export function PWAInstaller() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstall, setShowInstall] = useState(false)
  const pathname = usePathname()

  // Check if current route allows showing the install prompt
  const isAllowedRoute = ALLOWED_ROUTES.some(route => pathname === route)

  useEffect(() => {
    // Capture the beforeinstallprompt event once for manual install later
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Remove listener after capturing — no need to capture again
      window.removeEventListener("beforeinstallprompt", handler)
    }

    window.addEventListener("beforeinstallprompt", handler)

    return () => {
      window.removeEventListener("beforeinstallprompt", handler)
    }
  }, [])

  useEffect(() => {
    // Defer all setState calls to avoid cascading renders within the effect body
    let cancelled = false
    queueMicrotask(() => {
      if (cancelled) return
      // Only show install UI on allowed routes
      if (!isAllowedRoute) {
        setShowInstall(false)
        return
      }

      // Check if user previously dismissed the prompt
      const dismissedAt = localStorage.getItem(DISMISS_KEY)
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt, 10)
        // Don't show again for 7 days after dismissal
        if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
          return
        }
      }

      // Check if already installed. The manifest declares "fullscreen" (kiosk
      // fleet alignment — MDM_REFERENCE §15 R4); installed instances report
      // whichever mode they actually got, so match both.
      if (window.matchMedia("(display-mode: fullscreen), (display-mode: standalone)").matches) {
        setShowInstall(false)
        return
      }

      // Show install UI if we have a deferred prompt
      if (deferredPrompt) {
        setShowInstall(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [isAllowedRoute, deferredPrompt])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    // Now we call prompt() which is what the browser expects
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === "accepted") {
      setShowInstall(false)
    }

    setDeferredPrompt(null)
  }

  if (!showInstall) return null

  // On /login the default bottom-right slot covers the keypad's Login key
  // (and the whole bottom keypad row on phones) — the screen's primary action.
  // There: top of the screen on phones, bottom-left (over the branding pane)
  // on tablets/desktop. Everywhere else: default bottom-right.
  const positionClass =
    pathname === "/login"
      ? "fixed top-4 left-4 right-4 z-50 md:top-auto md:bottom-4 md:right-auto md:left-4 md:max-w-sm"
      : "fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"

  return (
    <div className={positionClass}>
      <Card className="border-2 border-navy-700 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="bg-navy-700 rounded-full p-2">
              <Download className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">Install Cherish Dining</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Install this app on your device for quick access and offline use
              </p>
              <div className="flex gap-2 mt-3">
                <Button onClick={handleInstall} size="sm" className="flex-1 bg-navy-700 hover:bg-navy-600">
                  Install
                </Button>
                <Button
                  onClick={() => {
                    localStorage.setItem(DISMISS_KEY, Date.now().toString())
                    setShowInstall(false)
                  }}
                  size="sm"
                  variant="outline"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
